import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, DollarSign, QrCode, RefreshCw, Copy, Gauge, ImagePlus, X, ChevronUp, ChevronDown, Link as LinkIcon } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { AdminHeader, TableWrap, Th, Td, Tr, StatusChip, Spinner, EmptyRow, Modal, Field } from "@/components/admin/ui";

const TYPES = [
  { value: "racing_sim", label: "Racing Sim" },
  { value: "flight_sim", label: "Flight Sim" },
  { value: "vr_station", label: "VR Station" },
  { value: "gaming_station", label: "Gaming Station" },
  { value: "golf_sim", label: "Golf Sim" },
];
const isSimType = (t) => t === "racing_sim" || t === "flight_sim" || t === "golf_sim";
const emptyForm = { name: "", type: "racing_sim", description: "", hourly_rate: "", is_active: true, simhub_enabled: false, pairing_code: "", motion_available: false, motion_price: "", image_url: "", shared_enabled: false, shared_with: [] };

const absoluteImageUrl = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${u}`;
};

export default function AdminStations() {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [qrStation, setQrStation] = useState(null);
  const fileInputRef = useRef(null);

  const load = () => {
    apiClient.get("/admin/stations").then(({ data }) => setStations(data)).catch(() => {}).finally(() => setLoading(false));  };
  useEffect(load, []);

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= stations.length) return;
    const next = [...stations];
    [next[index], next[target]] = [next[target], next[index]];
    setStations(next);
    try {
      await apiClient.put("/admin/stations/reorder", { ordered_ids: next.map((s) => s.id) });
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not reorder"));
      load();
    }
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, type: s.type, description: s.description, hourly_rate: String(s.hourly_rate), is_active: s.is_active, simhub_enabled: !!s.simhub_enabled, pairing_code: s.pairing_code || "", motion_available: !!s.motion_available, motion_price: s.motion_price != null ? String(s.motion_price) : "", image_url: s.image_url || "", shared_enabled: (s.shared_with || []).length > 0, shared_with: s.shared_with || [] });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.description || !form.hourly_rate) { toast.error("Fill in all fields"); return; }
    setSaving(true);
    try {
      const simhub = isSimType(form.type) ? form.simhub_enabled : false;
      const motionOn = isSimType(form.type) ? form.motion_available : false;
      const payload = {
        name: form.name, description: form.description, hourly_rate: Number(form.hourly_rate), is_active: form.is_active,
        simhub_enabled: simhub, pairing_code: simhub ? (form.pairing_code || undefined) : undefined,
        motion_available: motionOn,
        motion_price: motionOn ? Number(form.motion_price || 0) : 0,
        image_url: form.image_url || null,
        shared_with: form.shared_enabled ? form.shared_with : [],
      };
      if (editing) {
        await apiClient.put(`/admin/stations/${editing.id}`, payload);
        toast.success("Station updated");
      } else {
        await apiClient.post("/admin/stations", { ...payload, type: form.type });
        toast.success("Station created");
      }
      setModalOpen(false);
      load();
    } catch (err) { toast.error(getErrorMessage(err, "Save failed")); }
    finally { setSaving(false); }
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!editing) {
      toast.error("Save the station first, then add an image");
      return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await apiClient.post(`/admin/stations/${editing.id}/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.image_url }));
      setEditing((s) => (s ? { ...s, image_url: data.image_url } : s));
      toast.success("Image uploaded");
      load();
    } catch (err) { toast.error(getErrorMessage(err, "Upload failed")); }
    finally { setUploading(false); }
  };

  const clearImage = () => {
    if (!editing) { setForm((f) => ({ ...f, image_url: "" })); return; }
    setForm((f) => ({ ...f, image_url: "" }));
  };

  const regenerateKey = async () => {
    if (!editing) return;
    try {
      const { data } = await apiClient.post(`/admin/stations/${editing.id}/simhub/regenerate-key`);
      setEditing({ ...editing, simhub_api_key: data.simhub_api_key, simhub_enabled: true });
      toast.success("API key regenerated");
      load();
    } catch (err) { toast.error(getErrorMessage(err, "Failed")); }
  };

  const copy = (text, label) => { navigator.clipboard?.writeText(text); toast.success(`${label} copied`); };

  const remove = async (s) => {
    if (!window.confirm(`Delete station "${s.name}"?`)) return;
    try {
      await apiClient.delete(`/admin/stations/${s.id}`);
      toast.success("Station deleted");
      load();
    } catch (err) {
      const msg = getErrorMessage(err, "Delete failed");
      const status = err?.response?.status;
      if (status === 400 && /active booking/i.test(msg)) {
        if (window.confirm(`${msg}\n\nForce delete and cancel/adjust those bookings?`)) {
          try {
            await apiClient.delete(`/admin/stations/${s.id}?force=true`);
            toast.success("Station force-deleted");
            load();
          } catch (err2) { toast.error(getErrorMessage(err2, "Force delete failed")); }
        }
      } else {
        toast.error(msg);
      }
    }
  };

  const pairUrl = (code) => `${window.location.origin}/pair/${code}`;

  if (loading) return <Spinner label="Loading stations" />;

  return (
    <div>
      <AdminHeader title="Gaming Stations" subtitle={`${stations.length} stations`}
        right={<button onClick={openCreate} className="neon-btn px-5 py-2.5" data-testid="add-station"><Plus className="w-4 h-4" /> Add Station</button>} />
      <TableWrap>
        <thead><Tr><Th>Order</Th><Th>Name</Th><Th>Type</Th><Th>Rate</Th><Th>SimHub</Th><Th>Status</Th><Th className="text-right">Actions</Th></Tr></thead>
        <tbody>
          {stations.length === 0 ? <EmptyRow colSpan={7} text="No stations yet" /> : stations.map((s, idx) => (
            <Tr key={s.id}>
              <Td>
                <div className="flex items-center gap-1">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-white/10 text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed" title="Move up" data-testid={`station-up-${s.id}`}><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={() => move(idx, 1)} disabled={idx === stations.length - 1} className="p-1 rounded hover:bg-white/10 text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed" title="Move down" data-testid={`station-down-${s.id}`}><ChevronDown className="w-4 h-4" /></button>
                </div>
              </Td>
              <Td className="text-white font-semibold" data-testid={`station-name-${s.id}`}>{s.name}</Td>
              <Td className="capitalize">{s.type?.replace(/_/g, " ")}</Td>
              <Td><span style={{ color: "#00e5ff" }}><DollarSign className="w-3.5 h-3.5 inline mb-0.5" />{s.hourly_rate}/hr</span></Td>
              <Td>
                {s.simhub_enabled
                  ? <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: "#00ff9d" }}><Gauge className="w-3.5 h-3.5" /> On</span>
                  : <span className="text-slate-600 text-xs">Off</span>}
              </Td>
              <Td><StatusChip status={s.is_active ? "active" : "inactive"} /></Td>
              <Td className="text-right">
                <div className="flex items-center justify-end gap-2">
                  {s.simhub_enabled && s.pairing_code && (
                    <button onClick={() => setQrStation(s)} className="p-2 rounded-lg hover:bg-white/10 text-emerald-400" title="Show QR" data-testid={`qr-station-${s.id}`}><QrCode className="w-4 h-4" /></button>
                  )}
                  <button onClick={() => openEdit(s)} className="p-2 rounded-lg hover:bg-white/10 text-cyan-400" data-testid={`edit-station-${s.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(s)} className="p-2 rounded-lg hover:bg-red-500/10 text-pink-400" data-testid={`delete-station-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </Td>
            </Tr>
          ))}
        </tbody>
      </TableWrap>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Edit Station" : "Add Station"} testId="station-modal">
        <Field label="Name"><input className="input-cyber" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="station-form-name" /></Field>
        <Field label="Type">
          <select className="input-cyber capitalize" value={form.type} disabled={!!editing}
            onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="station-form-type">
            {TYPES.map((t) => <option key={t.value} value={t.value} className="bg-slate-900">{t.label}</option>)}
          </select>
        </Field>
        <Field label="Description"><textarea className="input-cyber min-h-[70px] resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="station-form-desc" /></Field>
        <Field label="Hourly Rate ($)"><input type="number" min="0" step="0.5" className="input-cyber" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} data-testid="station-form-rate" /></Field>

        <Field label="Cover image (optional — overrides the default icon on booking cards)">
          <div className="flex items-start gap-3">
            {form.image_url ? (
              <div className="relative w-24 h-24 rounded-xl overflow-hidden border border-white/10 shrink-0">
                <img src={absoluteImageUrl(form.image_url)} alt="Station cover" className="w-full h-full object-cover" data-testid="station-image-preview" />
                <button type="button" onClick={clearImage} className="absolute top-1 right-1 bg-black/70 rounded-full p-1 hover:bg-black" title="Remove image" data-testid="station-image-clear"><X className="w-3 h-3 text-white" /></button>
              </div>
            ) : (
              <div className="w-24 h-24 rounded-xl border border-dashed border-white/15 flex items-center justify-center text-slate-500 shrink-0">
                <ImagePlus className="w-6 h-6" />
              </div>
            )}
            <div className="flex-1">
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} data-testid="station-image-input" />
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading || !editing} className="neon-outline-btn px-4 py-2" data-testid="station-image-upload">
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</> : <><ImagePlus className="w-4 h-4" /> {form.image_url ? "Replace image" : "Upload image"}</>}
              </button>
              <p className="text-slate-500 text-xs mt-2">
                {editing ? "PNG, JPG, WEBP or GIF, up to 5MB. Shown on web and mobile booking cards." : "Save the station first to enable image upload."}
              </p>
            </div>
          </div>
        </Field>

        <label className="flex items-center gap-3 mb-4 cursor-pointer">
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="station-form-active" />
          <span className="text-slate-200">Active (available for booking)</span>
        </label>

        <div className="rounded-xl p-4 mb-5" style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.25)" }}>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.shared_enabled} onChange={(e) => setForm({ ...form, shared_enabled: e.target.checked })} data-testid="station-form-shared" />
            <span className="text-white font-semibold flex items-center gap-2"><LinkIcon className="w-4 h-4" style={{ color: "#8b5cf6" }} /> Shared asset (dual-use hardware)</span>
          </label>
          <p className="text-slate-400 text-xs mt-2">Link stations that are physically the same unit used for different purposes. Booking any one makes the others unavailable for that time — they can never be booked at once.</p>
          {form.shared_enabled && (
            <div className="mt-3 max-h-48 overflow-y-auto scrollbar-thin space-y-1.5" data-testid="shared-station-list">
              {stations.filter((s) => !editing || s.id !== editing.id).length === 0 ? (
                <p className="text-slate-500 text-xs">{editing ? "No other stations to link yet." : "Save this station first, then edit it to link shared stations."}</p>
              ) : stations.filter((s) => !editing || s.id !== editing.id).map((s) => {
                const on = form.shared_with.includes(s.id);
                return (
                  <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-white/5" data-testid={`shared-option-${s.id}`}>
                    <input type="checkbox" checked={on}
                      onChange={(e) => setForm((f) => ({ ...f, shared_with: e.target.checked ? [...f.shared_with, s.id] : f.shared_with.filter((x) => x !== s.id) }))} />
                    <span className="text-slate-200 text-sm">{s.name}</span>
                    <span className="text-slate-500 text-xs capitalize">· {String(s.type).replace("_", " ")}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {isSimType(form.type) && (
          <div className="rounded-xl p-4 mb-5" style={{ background: "rgba(0,255,157,0.06)", border: "1px solid rgba(0,255,157,0.25)" }}>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.simhub_enabled} onChange={(e) => setForm({ ...form, simhub_enabled: e.target.checked })} data-testid="station-form-simhub" />
              <span className="text-white font-semibold flex items-center gap-2"><Gauge className="w-4 h-4" style={{ color: "#00ff9d" }} /> Enable SimHub telemetry</span>
            </label>
            {form.simhub_enabled && (
              <div className="mt-4 space-y-3">
                <Field label="Pairing code (used in the QR — leave blank to auto-generate)">
                  <input className="input-cyber" placeholder="auto" value={form.pairing_code} onChange={(e) => setForm({ ...form, pairing_code: e.target.value })} data-testid="station-form-pairing-code" />
                </Field>
                {editing?.simhub_api_key && (
                  <div>
                    <p className="text-slate-400 text-xs uppercase tracking-widest mb-1">Station API key (for the bridge agent)</p>
                    <div className="flex items-center gap-2">
                      <code className="input-cyber !py-2 text-xs truncate flex-1" data-testid="station-api-key">{editing.simhub_api_key}</code>
                      <button onClick={() => copy(editing.simhub_api_key, "API key")} className="neon-outline-btn px-3 py-2" title="Copy"><Copy className="w-4 h-4" /></button>
                      <button onClick={regenerateKey} className="neon-outline-btn px-3 py-2" title="Regenerate" data-testid="regenerate-key"><RefreshCw className="w-4 h-4" /></button>
                    </div>
                  </div>
                )}
                {editing && !editing?.simhub_api_key && <p className="text-slate-500 text-xs">Save to generate the station API key.</p>}
              </div>
            )}
          </div>
        )}

        {isSimType(form.type) && (
          <div className="rounded-xl p-4 mb-5" style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.25)" }}>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.motion_available} onChange={(e) => setForm({ ...form, motion_available: e.target.checked })} data-testid="station-form-motion" />
              <span className="text-white font-semibold flex items-center gap-2"><Gauge className="w-4 h-4" style={{ color: "#c4b5fd" }} /> Motion simulator available</span>
            </label>
            {form.motion_available && (
              <div className="mt-4">
                <Field label="Motion add-on price per hour ($)">
                  <input type="number" min="0" step="0.5" className="input-cyber" placeholder="e.g. 15" value={form.motion_price} onChange={(e) => setForm({ ...form, motion_price: e.target.value })} data-testid="station-form-motion-price" />
                </Field>
                <p className="text-slate-500 text-xs -mt-2">Customers can opt in to motion at booking time; the fee is added on top of the hourly rate.</p>
              </div>
            )}
          </div>
        )}

        <button onClick={save} disabled={saving} className="neon-btn w-full py-3" data-testid="station-form-save">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : editing ? "Save Changes" : "Create Station"}
        </button>
      </Modal>

      <Modal open={!!qrStation} onClose={() => setQrStation(null)} title={`Pair QR — ${qrStation?.name || ""}`} testId="qr-modal">
        {qrStation && (
          <div className="text-center">
            <p className="text-slate-400 text-sm mb-4">Print this and place it on the station. Customers scan it with their phone camera to pair their session.</p>
            <div className="bg-white rounded-2xl p-5 inline-block" data-testid="qr-canvas">
              <QRCodeCanvas value={pairUrl(qrStation.pairing_code)} size={220} includeMargin />
            </div>
            <div className="flex items-center gap-2 mt-4">
              <code className="input-cyber !py-2 text-xs truncate flex-1">{pairUrl(qrStation.pairing_code)}</code>
              <button onClick={() => copy(pairUrl(qrStation.pairing_code), "Pair link")} className="neon-outline-btn px-3 py-2"><Copy className="w-4 h-4" /></button>
            </div>
            <button onClick={() => window.print()} className="neon-btn w-full py-3 mt-4" data-testid="print-qr">Print QR</button>
          </div>
        )}
      </Modal>
    </div>
  );
}
